import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, ExternalLink } from 'lucide-react';

import { API } from '../lib/api';

export default function SharedViewPage() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [shared, setShared] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/shared/${shareId}`)
      .then(res => setShared(res.data))
      .catch(() => setShared(null))
      .finally(() => setLoading(false));
  }, [shareId]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="animate-pulse text-slate-400">Loading...</div>
    </div>
  );

  if (!shared) return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-4">
      <p className="text-slate-500">This shared link has expired or doesn't exist.</p>
      <button onClick={() => navigate('/')} className="text-sm text-slate-700 underline">Go to GYNOVATE AI/SI</button>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center text-white font-bold text-sm">G</div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">GYNOVATE AI/SI</h1>
              <p className="text-xs text-slate-400">AI/SI Copilot for Doctors · by Dr. Vikram Dukle</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/chat')}
            className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors"
            data-testid="try-gynovate-btn"
          >
            Try GYNOVATE AI/SI <ExternalLink className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Query */}
        {shared.query && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-slate-100 border border-slate-200">
            <p className="text-xs text-slate-400 mb-1">Question</p>
            <p className="text-sm text-slate-700 font-medium">{shared.query}</p>
          </div>
        )}

        {/* AI Response */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="shared-content">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center">
              <span className="text-amber-700 text-xs font-bold">AI</span>
            </div>
            <span className="text-sm font-semibold text-slate-900">GYNOVATE AI/SI</span>
            <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{shared.mode}</span>
          </div>
          <div className="prose prose-sm prose-slate max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{shared.content}</ReactMarkdown>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 mt-8">
          AI responses are clinical decision support — always verify with your judgement.
        </p>
      </div>
    </div>
  );
}
